import { apiService } from './api';

export interface PricingIncludedResources {
  vcpu?: number;
  memoryMb?: number;
  storageGb?: number;
  transferGb?: number;
  // Kubiy AI (FAMILY#AI)
  messages?: number;
  documents?: number;
  storageMb?: number;
  // AI_SERVICE (esquema anterior)
  messagesPerMonth?: number;
  documentsLimit?: number;
}

export interface PricingProviderConfiguration {
  provider?: string;
  resourceType?: string;
  instanceType?: string;
  volumeType?: string;
}

export interface PricingPlan {
  planCode: string;
  planName: string;
  displayName?: string;
  priceId?: string;
  variantCode?: string;
  variantName?: string;
  variantOrder?: number;
  typeFamily?: string;
  osType?: string;
  currency?: string;
  billingInterval?: string;
  billingModel?: string;
  sortOrder?: number;
  priceAmountMinor?: number;
  priceAmount?: number;
  includedResources?: PricingIncludedResources;
  providerConfiguration?: PricingProviderConfiguration;
  raw: Record<string, unknown>;
}

export interface PricingPlanPrice {
  priceAmountMinor?: number;
  priceMonthly?: number;
  includedResources?: PricingIncludedResources;
  raw: Record<string, unknown>;
}

export interface PricingAddon {
  priceId?: string;
  addonCode: string;
  addonName: string;
  description?: string;
  currency?: string;
  billingInterval?: string;
  billingModel?: string;
  priceAmountMinor?: number;
  priceAmount?: number;
  status?: string;
  compatiblePlans?: string[];
  configuration?: Record<string, unknown>;
  raw: Record<string, unknown>;
}

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const toText = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
};

const minorToMajor = (value?: number): number | undefined => {
  if (value === undefined) return undefined;
  return value / 100;
};

const toResources = (value: unknown): PricingIncludedResources | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const resources = value as Record<string, unknown>;
  return {
    vcpu: toNumber(resources.vcpu),
    memoryMb: toNumber(resources.memoryMb),
    storageGb: toNumber(resources.storageGb),
    transferGb: toNumber(resources.transferGb),
    messages: toNumber(resources.messages),
    documents: toNumber(resources.documents),
    storageMb: toNumber(resources.storageMb),
    messagesPerMonth: toNumber(resources.messagesPerMonth),
    documentsLimit: toNumber(resources.documentsLimit),
  };
};

const toProviderConfiguration = (value: unknown): PricingProviderConfiguration | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const config = value as Record<string, unknown>;
  return {
    provider: toText(config.provider),
    resourceType: toText(config.resourceType),
    instanceType: toText(config.instanceType),
    volumeType: toText(config.volumeType),
  };
};

const toRecordArray = (payload: unknown): Record<string, unknown>[] => {
  if (Array.isArray(payload)) {
    return payload.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'));
  }

  if (!payload || typeof payload !== 'object') return [];

  const record = payload as Record<string, unknown>;
  const keys = ['plans', 'items', 'data', 'results'];
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) {
      return value.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'));
    }
  }

  // Fallback: el endpoint puede devolver un solo plan como objeto.
  if (record.planCode || record.planName) return [record];

  return [];
};

export class PricingService {
  private baseEndpoint = '/pricing';
  private addonsEndpoint = '/addons/pricing';

  async getPlans(typeFamily: string, osType: string, osFamily?: string): Promise<PricingPlan[]> {
    const params = new URLSearchParams({ typeFamily, osType });
    if (osFamily) params.set('osFamily', osFamily);
    const response = await apiService.get<unknown>(`${this.baseEndpoint}?${params.toString()}`);
    const plans = toRecordArray(response);

    return plans
      .map((record) => {
        const planCode = toText(record.planCode) || toText(record.code) || toText(record.id);
        if (!planCode) return null;

        const priceAmountMinor =
          toNumber(record.priceAmount) ??
          toNumber(record.priceAmountMinor) ??
          toNumber(record.amountMinor);

        return {
          planCode,
          planName: toText(record.planName) || toText(record.name) || toText(record.title) || planCode,
          displayName: toText(record.displayName),
          priceId: toText(record.priceId),
          variantCode: toText(record.variantCode),
          variantName: toText(record.variantName),
          variantOrder: toNumber(record.variantOrder),
          typeFamily: toText(record.typeFamily),
          osType: toText(record.osType),
          currency: toText(record.currency) || 'MXN',
          billingInterval: toText(record.billingInterval),
          billingModel: toText(record.billingModel),
          sortOrder: toNumber(record.sortOrder),
          priceAmountMinor,
          priceAmount: minorToMajor(priceAmountMinor),
          includedResources: toResources(record.includedResources),
          providerConfiguration: toProviderConfiguration(record.providerConfiguration),
          raw: record,
        } as PricingPlan;
      })
      .filter((plan): plan is PricingPlan => Boolean(plan))
      .sort((a, b) => {
        const sortDelta = (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER);
        if (sortDelta !== 0) return sortDelta;
        return (a.variantOrder ?? Number.MAX_SAFE_INTEGER) - (b.variantOrder ?? Number.MAX_SAFE_INTEGER);
      });
  }

  async getPlanPricing(
    planCode: string,
    typeFamily: string,
    osType: string,
    osFamily?: string
  ): Promise<PricingPlanPrice | null> {
    const params = new URLSearchParams({ typeFamily, osType });
    if (osFamily) params.set('osFamily', osFamily);
    const response = await apiService.get<unknown>(
      `${this.baseEndpoint}/${encodeURIComponent(planCode)}?${params.toString()}`
    );
    if (!response || typeof response !== 'object') return null;
    const record = response as Record<string, unknown>;

    const priceAmountMinor =
      toNumber(record.priceAmount) ??
      toNumber(record.priceAmountMinor) ??
      toNumber(record.amountMinor);

    return {
      priceAmountMinor,
      priceMonthly: minorToMajor(priceAmountMinor),
      includedResources: toResources(record.includedResources),
      raw: record,
    };
  }

  async getPlanAddons(typeFamily: string, osType: string, planCode: string, osFamily?: string): Promise<PricingAddon[]> {
    const params = new URLSearchParams({ typeFamily, osType, planCode });
    if (osFamily) params.set('osFamily', osFamily);
    const response = await apiService.get<unknown>(`${this.addonsEndpoint}?${params.toString()}`);
    const items = toRecordArray(response);

    return items
      .map((record) => {
        const addonCode = toText(record.addonCode) || toText(record.code) || toText(record.id);
        if (!addonCode) return null;

        const priceAmountMinor =
          toNumber(record.priceAmount) ??
          toNumber(record.priceAmountMinor) ??
          toNumber(record.amountMinor);

        const compatiblePlansRaw = record.compatiblePlans;
        const compatiblePlans = Array.isArray(compatiblePlansRaw)
          ? compatiblePlansRaw
              .map((plan) => toText(plan))
              .filter((plan): plan is string => Boolean(plan))
          : undefined;

        return {
          priceId: toText(record.priceId),
          addonCode,
          addonName: toText(record.addonName) || toText(record.name) || addonCode,
          description: toText(record.description),
          currency: toText(record.currency) || 'MXN',
          billingInterval: toText(record.billingInterval),
          billingModel: toText(record.billingModel),
          priceAmountMinor,
          priceAmount: minorToMajor(priceAmountMinor),
          status: toText(record.status),
          compatiblePlans,
          configuration:
            record.configuration && typeof record.configuration === 'object'
              ? (record.configuration as Record<string, unknown>)
              : undefined,
          raw: record,
        } as PricingAddon;
      })
      .filter((addon): addon is PricingAddon => Boolean(addon));
  }
}

export const pricingService = new PricingService();
